/**
 * VenueDetailPage — the tabbed venue home. Details, Marketing and Workshops are
 * live; Relationship is a reserved stub.
 *
 * Mounted standalone at `/admin/venues/:venueId`, from the unified Venues
 * page. What a viewer may edit comes from the server on the venue
 * itself (`canEdit`, `canWriteMarketing`), so a read-only viewer sees no
 * edit controls and a marketing user can edit the Marketing tab only.
 */
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import type { VenuePatch } from '../../../api/venues';
import { workshopShellFromPathname } from '../workshopDetailUrlFor';

import { DriftBanner } from './detail/DriftBanner';
import { LockedHintContext, lockedHintFor } from './detail/locked-hint';
import { LockedBanner } from './detail/LockedBanner';
import { OpenTasksBanner } from './detail/OpenTasksBanner';
import { useVenuePhotoFlow } from './detail/photo/use-venue-photo-flow';
import { TaxRateBanner } from './detail/TaxRateBanner';
import { VENUES_ROUTE } from './detail/venue-detail-path';
import { TAB_SLUGS, tabFromSlug, type Tab } from './detail/venue-tabs';
import { VenueDetailHeader } from './detail/VenueDetailHeader';
import { VenueTabBar } from './detail/VenueTabBar';
import { VenueTabPanel } from './detail/VenueTabPanel';
import {
  useApplyVenue,
  useDeactivateVenue,
  useRefreshVenue,
  useUpdateVenue,
  useVenue,
} from './useVenue';

export default function VenueDetailPage() {
  const { venueId = '', tab: tabParam } = useParams<{ venueId: string; tab?: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The active tab is driven by the `:tab` URL segment so tabs are deep-linkable
  // and survive refresh / browser back. Unknown/missing → Details.
  const tab = tabFromSlug(tabParam);
  // The Workshops tab's create-drawer redirect needs the shell.
  const shell = workshopShellFromPathname(pathname);
  // Absolute (not relative `..`) so navigation is unaffected by whether a
  // `:tab` segment is present — relative `..` resolves differently per depth.
  const venuesPath = VENUES_ROUTE;
  const setTab = (t: Tab) =>
    navigate(`${venuesPath}/${encodeURIComponent(venueId)}/${TAB_SLUGS[t]}`);

  const { data: venue, isLoading, error } = useVenue(venueId);
  const update = useUpdateVenue(venueId);
  const deactivate = useDeactivateVenue(venueId);
  // Kept here, above the tabs, so an open crop or a running upload survives a tab switch.
  const photoFlow = useVenuePhotoFlow(venueId, {
    apply: useApplyVenue(venueId),
    refresh: useRefreshVenue(venueId),
  });

  const save = async (patch: VenuePatch) => {
    await update.mutateAsync(patch);
  };
  const onDelete = async () => {
    await deactivate.mutateAsync();
    navigate(venuesPath);
  };

  if (isLoading) {
    return <div style={{ padding: 24, color: 'var(--nb-subtle)' }}>Loading venue…</div>;
  }
  if (error || !venue) {
    return (
      <div style={{ padding: 24 }}>
        <Link to={venuesPath} style={{ color: 'var(--nb-primary)', fontSize: 12.5 }}>
          ‹ Back to venues
        </Link>
        <div style={{ marginTop: 12, color: 'var(--nb-warn)' }}>
          {error instanceof Error ? error.message : 'Venue not found.'}
        </div>
      </div>
    );
  }

  const lockedHint = lockedHintFor(venue);
  // Marketing explains itself in its own panel, and Workshops is gated by its own permission.
  const showLockedBanner = tab !== 'Marketing' && tab !== 'Workshops' && !venue.canEdit;
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <VenueDetailHeader name={venue.name} stage={venue.stage} venuesPath={venuesPath} />

      <TaxRateBanner taxPercent={venue.taxPercent} />
      <DriftBanner drift={venue.drift ?? []} />
      <OpenTasksBanner venueId={venueId} />
      {showLockedBanner && <LockedBanner hint={lockedHint} />}
      <VenueTabBar tab={tab} setTab={setTab} />

      <LockedHintContext.Provider value={lockedHint}>
        <VenueTabPanel
          tab={tab}
          venue={venue}
          venueId={venueId}
          photoFlow={photoFlow}
          shell={shell}
          save={save}
          onDelete={onDelete}
        />
      </LockedHintContext.Provider>
    </div>
  );
}
